import { emptyApplicationsArray } from '@common/constants'
import { F_RATING_SYSTEM, F_VERSION_SELECTOR } from '@common/featureFlags/access'
import CheckAccess from '@common/featureFlags/CheckAccess'
import useAccessLevel from '@common/featureFlags/useAccessLevel'
import { useAppDispatch, useAppSelector, useErrorMessages, useIframe, useMyApp, useSession, useTranslate } from '@common/hooks'
import findFirstUnusedSlot from '@common/utils/findFirstUnusedSlot'
import getPermissions from '@common/utils/getPermissions'
import { getUuidFromAppSlug } from '@common/utils/uuid'
import { fetchApplicationInfo, setAutoupdate } from '@redux/slices/application/application'
import { unmountAppRating } from '@redux/slices/applicationRating/applicationRating'
import { unmountVersionsInfo } from '@redux/slices/appVersions'
import { fetchUserSlots } from '@redux/slices/userSlots/userSlots'
import getDefaultValues from '@utils/getDefaultValues'
import getSettings from '@utils/getSettings'
import Head from 'next/head'
import { createContext, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import type { IOnSubmitParams } from './Application.types'
import ApplicationNoSsrHOC from './ApplicationNoSsrHOC'
import classNames from './styles.module.scss'
import AppHeader from './widgets/AppHeader'
import AppInfo from './widgets/AppInfo'
import ApplicationTabs from './widgets/ApplicationTabs'
import AppRatingInitialization from './widgets/AppRatingInitialization'
import type { IApiError } from './widgets/AppSettingsForm'
import {
  AppSettingsForm,
  PermissionConfirmationModal,
  SlotConfirmationModal,
  submitHandler,
} from './widgets/AppSettingsForm'
import type { IInputs, SubmitAction } from './widgets/AppSettingsForm/appSettingsForm.types'
import BroadcastOverlays from './widgets/BroadcastOverlays'
import MarkdownDescription from './widgets/MarkdownDescription'
import ReadMoreWrapper from './widgets/ReadMoreWrapper'
import Screenshots from './widgets/Screenshots'
import VersionSelector from './widgets/VersionSelector'


export interface IProps {
  autoclose: string,
}


export const VersionContext = createContext<{ version?: string, versionSelectorHasAccess: boolean }>({
  version: undefined,
  versionSelectorHasAccess: false,
})


function Application({ autoclose }: IProps): JSX.Element {
  const { translate } = useTranslate()
  const dispatch = useAppDispatch()
  const [versionId, setVersionId] = useState<string | undefined>(undefined)
  const application = useAppSelector(store => store.application?.data) as IApplication
  const slotId = useAppSelector(store => store.common.data.slotId)
  const userSlots = useAppSelector(store => store.userSlots.data?.items || emptyApplicationsArray)
  const versions = useAppSelector(store => store.appVersions?.data)

  const versionSelectorHasAccess = useAccessLevel(F_VERSION_SELECTOR)

  const getSettingsWithVersion = (settings: IApplicationSetting): IApplicationSetting => {
    if(!versionSelectorHasAccess) { return settings }
    if(application.installed && (!versionId || application?.installed?.id === versionId)) { return application.installed?.settings }
    const version = versions?.find(version => version.id === versionId)
    if(!version) { return application.installed?.settings || settings }
    return version.settings_schema
  }


  const settings = getSettings(getSettingsWithVersion(application.latest.settings))
  const permissions = getPermissions(application, userSlots) || []
  const defaultValues = getDefaultValues(settings, { ...application.settingsValues }, permissions)

  const methods = useForm<IInputs>({ defaultValues, mode: 'onChange' })
  const { handleSubmit, formState: { isSubmitting }, reset } = methods

  const [confirmationSlot, setConfirmationSlot] = useState<number | null>(null)
  const [showConfirmationModal, setConfirmationModal] = useState(false)
  const [showPermissionModal, setPermissionModal] = useState(false)
  const [serverError, setServerError] = useState<IApiError | null>(null)
  const { isMyApp } = useMyApp(application)
  const { isAuthorizedUser } = useSession()
  const { serverErrorMessage } = useErrorMessages(serverError)

  const submitAction = useMemo<SubmitAction>(() => {
    if(!versionSelectorHasAccess) {
      return application.hasUpdate
        ? 'upgrade'
        : isMyApp ? 'update' : 'install'
    }
    return !isMyApp
      ? 'install'
      : application?.installed?.id === versionId ? 'update' : 'upgrade'
  }, [application, isMyApp, versionId, versionSelectorHasAccess])

  const [slot, setSlot] = useState<number>(() => {
    if(application.isInstalled) { return application.slot as number }
    if(slotId) { return slotId }

    const usedSlots = userSlots.map(app => app.slot)
    return findFirstUnusedSlot(usedSlots, 10)
  })

  const { showLoginOverlay } = useIframe(null)

  const onSubmit = (params: IOnSubmitParams, confirmations: { slot: boolean, permissions: boolean }) => {
    return handleSubmit(async (fields) => {
      if(!isAuthorizedUser) {
        showLoginOverlay()
        return
      }
      if(confirmations.permissions && permissions.some(permission => permission.showWarningAlreadyUsed && fields[permission.key] === true)) {
        setPermissionModal(true)
        return
      }
      if(confirmations.slot && userSlots.some(app => app.slot === slot && app.id !== application.id)) {
        setConfirmationSlot(slot)
        setConfirmationModal(true)
        return
      }
      void dispatch(setAutoupdate(false))
      return await submitHandler(
        {
          fields, appId: application.id, slot, latestAppVersionId: application.latest?.id, appVersionId: versionSelectorHasAccess ? versionId : undefined,
        }, params, {
          setError: (apiError: IApiError) => {
            setServerError(apiError)
            void dispatch(setAutoupdate(true))
          },
          clearErrors: () => setServerError(null),
          onSuccess: () => {
            void dispatch(fetchUserSlots())
            dispatch(fetchApplicationInfo(getUuidFromAppSlug(application.id) as string))
              .then(({ payload }) => {
                const app = payload as IApplication
                const settings = getSettings(getSettingsWithVersion(app.latest.settings))
                const permissions = getPermissions(app, userSlots) || []
                const defaultValues = getDefaultValues(settings, { ...app.settingsValues }, permissions)
                reset(defaultValues)
              })
              .catch(() => {
                reset(defaultValues)
              })
              .finally(() => {
                void dispatch(setAutoupdate(true))
              })
          },
        }
      )
    })
  }

  const handleSetNewVersion = (versionId: string) => {
    if(!versions) { return }
    setVersionId(versionId)
    const version = versions.find(version => version.id === versionId)
    if(version) {
      const settings = getSettings(version.settings_schema)
      const defaultValues = getDefaultValues(settings, { ...application.settingsValues }, permissions)
      reset(defaultValues, { keepValues: false, keepDefaultValues: true })
    }
  }

  useEffect(() => {
    return () => {
      dispatch(unmountAppRating())
      dispatch(unmountVersionsInfo())
    }
  }, [])

  return (
    <>
      <Head>
        <title>
          {application.name}
        </title>
        <meta
          property="og:title"
          content={application.name}
          key="title"
        />
      </Head>
      <CheckAccess access={F_RATING_SYSTEM}>
        <AppRatingInitialization />
      </CheckAccess>
      <VersionContext.Provider value={{
        version: versionId,
        versionSelectorHasAccess,
      }}>
        <FormProvider {...methods}>
          <AppHeader
            slot={slot}
            setSlot={setSlot}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            submitAction={submitAction}
            preventWindowClose={autoclose !== 'true'}
          />
          <div className={classNames.wrapper}>
            {application && (
              <>
                <AppInfo application={application}>
                  <VersionSelector
                    value={versionId}
                    onChange={handleSetNewVersion}
                  />
                </AppInfo>
                <ApplicationTabs>
                  <ReadMoreWrapper>
                    {application.latest.description
                      ? application.latest.descriptionIsMarkdown
                        ? <MarkdownDescription description={application.latest.description}/>
                        : <p className={classNames.descriptionParagraph} data-test="app-description">
                          {application.latest.description}
                        </p>
                      : <p className={classNames.emptyDescription} data-test="app-no-description">
                          {translate('No description yet.')}
                        </p>
                    }
                  </ReadMoreWrapper>
                  {Boolean(Object.values(application.latest.broadcastOverlays || {}).length) && (
                    <>
                      <h3 className={classNames.title} data-test="app-broadcast-layers-header">{translate('Broadcast Overlays')}</h3>
                      <BroadcastOverlays />
                    </>
                  )}
                  {Boolean(application.latest?.screenshots?.length) && (
                    <>
                      <h3 className={classNames.title} data-test="app-screenshots-header">{translate('Screenshots')}</h3>
                      <Screenshots
                        screenshots={application.latest?.screenshots}
                      />
                    </>
                  )}
                  <h3 className={classNames.title} data-test="app-settings-header">{translate('Settings')}</h3>
                  <AppSettingsForm
                    application={application}
                    settings={settings}
                    serverErrorMessage={serverErrorMessage}
                    onSubmit={onSubmit}
                    permissions={permissions}
                    confirmationSlot={confirmationSlot}
                    preventWindowClose={autoclose !== 'true'}
                    submitAction={submitAction}
                  />
                </ApplicationTabs>
              </>
            )}
            {!application && (
              <h1>{translate('App not found')}</h1>
            )}
          </div>
          <PermissionConfirmationModal
            permissions={permissions}
            show={showPermissionModal}
            onHide={() => { setPermissionModal(false) }}
            onSubmit={onSubmit({ replaceSlot: true, submitAction, preventWindowClose: autoclose !== 'true' }, { slot: true, permissions: false })}
          />
          <SlotConfirmationModal
            confirmationSlot={confirmationSlot}
            showConfirmationModal={showConfirmationModal}
            onHide={() => {
              setConfirmationModal(false)
              setTimeout(() => {
                setConfirmationSlot(null)
              }, 500)
            }}
            onSubmit={onSubmit({ replaceSlot: true, submitAction, preventWindowClose: autoclose !== 'true' }, { slot: false, permissions: false })}
          />
        </FormProvider>
      </VersionContext.Provider>
    </>
  )
}

export default ApplicationNoSsrHOC(Application)
